import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import { CircularProgress, Box } from '@mui/material';
import store from './store';
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import theme from './theme/theme';

// Lazy load major components
const Login = React.lazy(() => import('./components/Login'));
const Register = React.lazy(() => import('./components/Register'));
const DashboardV2 = React.lazy(() => import('./components/DashboardV2'));
const AIFlowBuilder = React.lazy(() => import('./components/AIFlow/AIFlowBuilder'));

// Loading fallback component
const LoadingFallback = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}
  >
    <CircularProgress size={40} />
  </Box>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
      refetchOnWindowFocus: false, // Don't refetch when window gets focus
      refetchOnReconnect: false, // Don't refetch when network reconnects
      refetchInterval: false, // Don't automatically refetch on interval
      retry: 1, // Only retry failed requests once
    },
  },
});

function App() {
  // Ensure provider is always set to 'plivo' by default
  React.useEffect(() => {
    if (!localStorage.getItem('provider')) {
      localStorage.setItem('provider', 'plivo');
    }
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Provider store={store}>
            <Router>
              <ErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Switch>
                    <Route path="/login" component={Login} />
                    <Route path="/register" component={Register} />
                    <Route path="/ai-flow" component={AIFlowBuilder} />
                    <PrivateRoute path="/" component={DashboardV2} />
                    <Redirect from="*" to="/login" />
                  </Switch>
                </Suspense>
              </ErrorBoundary>
            </Router>
          </Provider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
